import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";

const COUNTDOWN_SECONDS = 3;

/**
 * In-progress commit (fund-request transferring chrome): spinner + cash
 * + "Transferring / your money".
 */
export const TransferringPage = memo(() => {
  const { t } = useTranslation("lola");
  const { theme } = useStore();

  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("transfer.transferring")}
              highlightColor={TITLE_COLOR}
              secudnary={t("transfer.yourMoney")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});

/**
 * Certified Lola sending interstitial (going back to WhatsApp). Gallery-safe:
 * the return CTA is inspectable and does not navigate away.
 */
export const SendingMoneyPage = memo(() => {
  const { t } = useTranslation("lola");
  const { theme } = useStore();
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  const colorConfig = theme.specialView ?? theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    backgroundIcon,
    title,
    subtitile,
    bodyCopy,
  } = colorConfig;

  useEffect(() => {
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? COUNTDOWN_SECONDS : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <RoundedCheckIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("sending.goingBack")}
              highlightColor={title}
              secudnary={t("sending.toWhatsApp")}
              secudnaryColor={subtitile ?? title}
            />
          </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <GradientText
            textColor={bodyCopy}
            as="p"
            className="footer-text"
            aria-live="polite"
          >
            {t("sending.countdown", { count: secondsLeft })}
          </GradientText>
          <Button
            type="button"
            size="large"
            className="lola-return-chat"
            background={backgroundBtn}
            color={textColorBtn}
          >
            {t("sending.toWhatsApp")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
